import React from 'react'
import { Radio, theme, Tooltip, Typography } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { semanticColors, spacing } from '@vacationtracker/shared/constants/ThemeSetting'

const { Text } = Typography

export interface SegmentedToggleOption {
  label: string
  value: string | boolean
}

export interface SegmentedToggleProps {
  label?: React.ReactNode
  tooltip?: React.ReactNode
  required?: boolean
  options: SegmentedToggleOption[]
  value?: string | boolean
  onChange?: (value: string | boolean) => void
  disabled?: boolean
  /** `mini` is 24px tall with 14px text and 8px side padding, for dense surfaces such as a popover.
   * Default `medium`, the control as it ships. */
  size?: 'medium' | 'mini'
  /** Below 576px wide, fills the available width with equal options that wrap their text, so a narrow
   * screen never pushes an option onto its own line. Wider screens are unchanged. */
  fitNarrowScreens?: boolean
}

/** Its own class rather than antd's `size="small"`, which keeps a 12px label in some themes. */
const MINI_CSS = `
.vt-segmented-toggle--mini .ant-radio-button-wrapper {
  height: 24px;
  line-height: 22px;
  padding-inline: ${spacing.xs}px;
  font-size: 14px;
}
`

const FIT_NARROW_CSS = `
@media (max-width: 575px) {
  .vt-segmented-toggle--fit-narrow {
    display: flex;
    width: 100%;
  }
  .vt-segmented-toggle--fit-narrow .ant-radio-button-wrapper {
    flex: 1 1 0;
    min-width: 0;
    height: auto;
    min-height: 32px;
    white-space: normal;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
`

/**
 * Vacation Tracker's segmented-button toggle. Replaces `YesNoSwitch` (misleadingly
 * named - it wrapped Radio.Group, not Switch) and the 15 raw `Radio.Group
 * optionType="button"` call sites the audit found building the same look independently
 * (see docs/component-audit/toggles.html). `options` generalizes beyond yes/no.
 */
export function SegmentedToggle({ label, tooltip, required = false, options, value, onChange, disabled = false, size = 'medium', fitNarrowScreens = false }: SegmentedToggleProps) {
  // From the token, not the static palette: the neutral constants are black at an opacity, which
  // on the dark theme's dark surface leaves the label barely legible.
  const { token } = theme.useToken()

  return (
    <div>
      {label && (
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xxs, marginBottom: spacing.xxs }}>
          {required && <Text style={{ color: semanticColors.colorError }}>*</Text>}
          <Text style={{ color: token.colorTextSecondary }}>{label}</Text>
          {tooltip && (
            <Tooltip title={tooltip}>
              <QuestionCircleOutlined style={{ color: token.colorTextSecondary }} />
            </Tooltip>
          )}
        </div>
      )}
      {size === 'mini' && <style>{MINI_CSS}</style>}
      {fitNarrowScreens && <style>{FIT_NARROW_CSS}</style>}
      <Radio.Group
        optionType="button"
        className={[size === 'mini' && 'vt-segmented-toggle--mini', fitNarrowScreens && 'vt-segmented-toggle--fit-narrow'].filter(Boolean).join(' ') || undefined}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
      >
        {options.map((option) => (
          <Radio.Button key={String(option.value)} value={option.value}>
            {option.label}
          </Radio.Button>
        ))}
      </Radio.Group>
    </div>
  )
}

export default SegmentedToggle
